import { memo, useRef, useState } from "react";
import useDeepCompareEffect from "../../hooks/useDeepCompareEffect";
import { DisaggregatedMetric } from "../../services/types";
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { getMetricGroupChartId } from "../../utils/dashboardUtils";
import { addCommas, formatValue, formatValueBase } from "../../utils/helpers";
import { Chart } from "chart.js";
import { processData } from "../../utils/chartUtils";

interface RaceEthnicityChartProps {
    indicators: DisaggregatedMetric[] | null;
    title: string;
    tab: string;
    chartName: string;
    height: string;
    barColor: string;
    reverseOrder: boolean;
    topFive?: boolean;
}

const RaceEthnicityChart = memo(({ indicators, title, tab, chartName, height, barColor, reverseOrder, topFive }: RaceEthnicityChartProps) => {
    const [data, setData] = useState<{ label: string, value: number, rawValue: number | string }[]>([]);
    const [barColors, setBarColors] = useState<string[]>([]);
    const backgroundColor = '#125687';
    // const formatter=(value: any) => `${formatValue(value, 'percentage')}`;

    useDeepCompareEffect(() => {
        if (!indicators) return;

        const indicatorData = indicators.find(data => data.id === getMetricGroupChartId(tab, chartName));
        const ethnicityData = indicators.find(data => data.id === 1135);
        
        if (!indicatorData) return;

        // Process chart data for labels and values
        const indicatorValues = processData(indicatorData);
        const ethnicityValues = ethnicityData ? processData(ethnicityData, 1652) : [];

        // Insert a blank entry between indicator and ethnicity data
        let allValues = [
            ...indicatorValues,
            { label: '', value: 0, rawValue: 0 },  // Blank entry
            ...ethnicityValues,
        ];

        // Take top 5 values if topFive is true
        if (topFive) {
            allValues = allValues.sort((a,b) => b.value - a.value).slice(0, 5);
        }

        // Conditionally reverse the order of `allValues` if `reverseOrder` is true
        if (reverseOrder) {
            allValues.reverse();
        }

        // set barColors
        const barColors = allValues.map(_ =>barColor);

        setData(allValues);
        setBarColors(barColors);
    }, [indicators]);


    // Register the plugin to all charts:
    Chart.register(ChartDataLabels);
    const chartRef = useRef<HTMLCanvasElement>(null);
    const chartInstance = useRef<Chart<"bar"> | null>(null);

    useDeepCompareEffect(() => {
        const ctx = chartRef.current?.getContext("2d");
        if (ctx) {
            // Destroy existing chart instance if it exists
            if (chartInstance.current) {
                chartInstance.current.destroy();
            }

            // Extract labels and data
            const labels = data.map(d => d.label);
            // const chartData = data.map(d => d.value);
            const rawValues = data.map(d => Number(d.rawValue));

            // Define a percentage to increase the maxValue by (e.g., 10%)
            // const paddingPercentage = 0.1;

            // Set max and min values for label padding. Ensure min and max are correctly set, avoiding negative start for x-axis
            // Calculate the maxValue with additional padding
            // const maxValue = Math.round(Math.max(...rawValues) * (1 + paddingPercentage));
            const minValue = Math.min(0, ...rawValues);

            // Use provided barColors or fallback to backgroundColor for all bars
            const colors = barColors && barColors.length === data.length
                ? barColors
                : new Array(data.length).fill(backgroundColor);

            chartInstance.current = new Chart(ctx, {
                type: "bar",
                data: {
                    labels: labels,
                    datasets: [
                        {
                            label: title,
                            data: rawValues,
                            backgroundColor: colors,
                        },
                    ],
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,  // Allows the chart to take the full height
                    indexAxis: 'y',
                    plugins: {
                        legend: {
                            display: false,
                        },
                        datalabels: {
                            labels: {
                                title: {
                                    // First label (formatted value)
                                    anchor: 'start',
                                    align: 'start',
                                    // @ts-ignore
                                    formatter: (value, context) => {
                                        const rawValue = data[context.dataIndex].value;
                                        const label = data[context.dataIndex].label;
                                        // Hide label if it's an empty string
                                        if (label === '') return null;

                                        return `${formatValueBase(rawValue, 'percentage')}`
                                    },
                                    font: {
                                        size: 11,
                                        weight: 'bolder',
                                    },
                                    color: '#4D4D4D',
                                    offset: 2,
                                },
                                value: {
                                    // Second label (raw value)
                                    anchor: 'end',   // Align to the end of the bar
                                    align: 'end',    // Align to the right end of the bar
                                    // @ts-ignore
                                    formatter: (value, context) => {
                                        const rawValue = data[context.dataIndex].rawValue;
                                        const name = indicators?.find(data => data.id === getMetricGroupChartId(tab, chartName));
            
                                        // Retrieve the current disaggregation item
                                        const disaggregation = name?.versions[0]?.disaggregations[context.dataIndex];

                                        const unit = name?.versions[0].unit;
            
                                        // Check if percent_calc exists with valid n and d values
                                        const n = disaggregation?.percent_calc?.n;
                                        const d = disaggregation?.percent_calc?.d;

                                        // Hide value label if the label is empty
                                        const label = data[context.dataIndex].label;
                                        if (label === '') return null;
            
                                        if (n !== undefined && d !== undefined) {
                                            // Use percent_calc.n and percent_calc.d for the label, similar to tooltip
                                            return `${formatValue(Number(n), unit ?? 'integer')}`;
                                        } else {
                                            // Fallback to rawValue and versionValue
                                            return `${formatValue(Number(rawValue), unit ?? 'integer')}`;
                                        }
                                    },
                                    font: {
                                        size: 11,
                                        weight: 'normal',
                                    },
                                    color: '#4D4D4D', // Adjust color as needed
                                    offset: 0,
                                },
                            },
                        },
                        tooltip: {
                            callbacks: {
                                label: (context) => {
                                    const rawValue = data[context.dataIndex].rawValue;
                                    const name = indicators?.find(data => data.id === getMetricGroupChartId(tab, chartName));
                                    const versionValue = name?.versions[0]?.value;

                                    // Retrieve the current disaggregation item
                                    const disaggregation = name?.versions[0]?.disaggregations[context.dataIndex];
                                    
                                    // Check if percent_calc exists with valid n and d values
                                    const n = disaggregation?.percent_calc?.n;
                                    const d = disaggregation?.percent_calc?.d;

                                    const label = data[context.dataIndex].label;

                                    // Hide tooltip label if the label is empty
                                    if (label === '') return '';

                                    if (n !== undefined && d !== undefined) {
                                        // Use percent_calc.n and percent_calc.d for the tooltip
                                        return `${addCommas(Number(n))} of ${addCommas(Number(d))}`;
                                    } else {
                                        // Fallback to rawValue and versionValue
                                        return `${addCommas(Number(rawValue))} of ${addCommas(Number(versionValue))}`;
                                    }
                                }
                            },
                            bodyColor: '#FFFFFF'
                        },
                    },
                    layout: {
                        padding: {
                            left: 0,
                            right: 40,
                            top: 10,
                            bottom: 10,
                        }
                    },
                    scales: {
                        x: {
                            beginAtZero: true,
                            min: minValue,
                            // max: maxValue,
                            ticks: {
                                display: true,
                                color: "#4D4D4D",
                                minRotation: 0,
                                maxRotation: 0,
                                font: {
                                    size: 11,
                                    weight: 'bold',
                                },
                                padding: 10,
                                callback: function (value: any) {
                                    return formatValue(value, 'integer');
                                }
                            },
                            grid: {
                                display: true,
                                color: '#D3D3D3',
                            },
                            border: {
                                display: false,
                                dash: [6, 4],
                            },
                        },
                        y: {
                            grid: {
                                display: false,
                                drawTicks: true,
                            },
                            border: {
                                display: false
                            },
                            ticks: {
                                minRotation: 0,  // Ensure labels are parallel to y-axis
                                maxRotation: 0,  // Ensure labels are parallel to y-axis
                                font: {
                                    size: 11,
                                    weight: 'bold',
                                },
                                padding: 25,
                                color: '#4D4D4D',
                                autoSkip: false,  // Ensure all labels are displayed
                                callback: function (value: any, index: number) { 
                                // Callback to wrap long text labels
                                    const maxLabelWidth = 150;
                                    const labels = this.chart.data.labels;
                                    if (!labels) {
                                        console.log(value);
                                        return;
                                    }
                                    const label = labels[index] as string;

                                    // Wrap long labels into multiple lines by words
                                    const lines = [];
                                    let currentLine = '';
                                    const words = label.split(/\s+/);

                                    for (let word of words) {
                                        const testLine = currentLine + (currentLine ? ' ' : '') + word;
                                        // Measure the width of the test line
                                        const testWidth = this.chart.ctx.measureText(testLine).width;

                                        if (testWidth > maxLabelWidth) {
                                            if (currentLine) {
                                                lines.push(currentLine);
                                            }
                                            currentLine = word;
                                        } else {
                                            currentLine = testLine;
                                        }
                                    }
                                    lines.push(currentLine);

                                    return lines;
                                }
                            },
                        },
                    },
                },
            });
        }
    }, [data, title, backgroundColor]);

    return (
        <div className="w-full">
            <div className={height ? height : `h-80`}> {/* Adjust height as needed */}
                <canvas ref={chartRef} className="w-full bg-[#F0F0F0]"/>
            </div>
        </div>
    );
});

export default RaceEthnicityChart;